import {
    Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
    Divider, Grid, IconButton, MenuItem, TextField,
    CircularProgress
} from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import { useEffect, useState } from "react";
import { LinkAssetConfirmDialog } from "./LinkAssetConfirmDialog";
import { useEditSubAssetsList } from "../stressEntry/stressEntryQueries";
import { Error } from "@material-ui/icons";

export const EditLinkAssetDialog = ({ open, onClose, asset }: any) => {
    const [selectedPrimaryAsset, setSelectedPrimaryAsset] = useState('');
    const [selectedSubAsset, setSelectedSubAsset] = useState<number[]>([]);
    const [openDialog, setOpenDialog] = useState(false);

    const {
        error: errorAsset,
        data: subAssets = [],
        isLoading: isLoadingAsset,
        isError: isErrorAsset,
    } = useEditSubAssetsList(asset.id);

    // ✅ Pre-populate already linked sub-assets when data loads
    useEffect(() => {
        if (subAssets?.length) {
            const preLinked = subAssets
                .filter((s: any) => s.is_linked)
                .map((s: any) => s.id);
            setSelectedSubAsset(preLinked);
            setSelectedPrimaryAsset(asset.id?.toString() || "");
        }
    }, [subAssets, asset.id]);

    if (isLoadingAsset) {
        return <CircularProgress />;
    }

    if (isErrorAsset) {
        return <Error />;
    }

    // ✅ Payload with checked values
    const payload = {
        sub_asset_ids: selectedSubAsset,
        asset_calibration_id: Number(selectedPrimaryAsset),
    };

    const namePayload = {
        sub_asset_names: selectedSubAsset.map((id) => {
            const a = subAssets.find((s: any) => s.id === id);
            return a ? a.asset_name : id;
        }),
    };

    const handlePrimaryAssetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedPrimaryAsset(e.target.value);
    };

    // ✅ Toggle check/uncheck
    const handleToggleSubAsset = (id: number) => {
        setSelectedSubAsset((prev) =>
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        );
    };

    const handleDialogOpen = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setSelectedSubAsset([]);
        setSelectedPrimaryAsset('');
        setOpenDialog(false);
        onClose();
    };

    const menuProps = {
        PaperProps: { style: { maxHeight: 350, width: 300 } },
        anchorOrigin: { vertical: "bottom", horizontal: "left" },
        transformOrigin: { vertical: "top", horizontal: "left" },
        getContentAnchorEl: null,
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Grid container alignItems="center">
                        <Grid item xs>Edit Linked Assets</Grid>
                        <Grid item>
                            <IconButton onClick={onClose}><CloseIcon /></IconButton>
                        </Grid>
                    </Grid>
                </DialogTitle>

                <Divider />

                <DialogContent dividers>
                    <Grid container spacing={4}>
                        {/* Primary asset */}
                        <Grid item xs={6} style={{ marginTop: 20 }}>
                            <TextField
                                id="primary_asset"
                                label="Select primary asset"
                                select
                                variant="filled"
                                value={selectedPrimaryAsset} // ✅ now bound to id
                                onChange={handlePrimaryAssetChange}
                                fullWidth
                                disabled
                            >
                                <MenuItem key={asset.id} value={asset.id}>
                                    {asset.asset_name}
                                </MenuItem>
                            </TextField>
                        </Grid>

                        {/* Sub assets */}
                        <Grid item xs={6} style={{ marginTop: 20 }}>
                            <TextField
                                id="sub_asset"
                                label="Select sub-assets"
                                select
                                variant="filled"
                                value={selectedSubAsset}
                                fullWidth
                                SelectProps={{
                                    multiple: true,
                                    MenuProps: menuProps as any,
                                    renderValue: (selected: any) => {
                                        if (!selected.length) return "None";
                                        return selected
                                            .map((id: number) => {
                                                const a = subAssets.find((s: any) => s.id === id);
                                                return a ? a.asset_name : id;
                                            })
                                            .join(", ");
                                    },
                                }}
                            >
                                {subAssets.map((item: any) => {
                                    const isChecked = selectedSubAsset.includes(item.id);
                                    return (
                                        <MenuItem
                                            key={item.id}
                                            value={item.id}
                                            onClick={() => handleToggleSubAsset(item.id)}
                                            disabled={item.in_use && !item.is_calibration}
                                        >
                                            <Checkbox checked={isChecked} />
                                            {item.asset_name}
                                        </MenuItem>
                                    );
                                })}
                            </TextField>
                        </Grid>

                        {/* Buttons */}
                        <Grid item xs={12}>
                            <Grid container justifyContent="center" spacing={2} style={{ marginTop: 24, marginBottom: 10 }}>
                                <Grid item>
                                    <Button onClick={onClose} variant="contained" color="secondary">CANCEL</Button>
                                </Grid>
                                <Grid item>
                                    <Button variant="contained" color="primary" onClick={handleDialogOpen}>
                                        UPDATE LINKED ASSETS
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </DialogContent>
            </Dialog>

            <LinkAssetConfirmDialog
                open={openDialog}
                onClose={handleCloseDialog}
                payload={payload}
                namepayload={namePayload}
                mode="edit"
                asset={asset}
            />
        </>
    );
};
